import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Brain } from "lucide-react";

const inp = "bg-pb-elevated border border-pb-border px-2 py-1.5 text-xs text-white focus:outline-none focus:border-pb-cyan w-20";

export const BrainQuotaPanel = () => {
  const [tiers, setTiers] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/brain-quotas").then(({ data }) => setTiers(data.tiers)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (key) => {
    const t = tiers[key];
    setBusy(true);
    try {
      await api.post("/admin/brain-quotas", { tier: key, quota: Number(t.quota), period: t.period, max_lvl: Number(t.max_lvl), extra_cost: Number(t.extra_cost || 0) });
      toast.success(`${t.label} brain quota updated`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const set = (key, field, val) => setTiers((s) => ({ ...s, [key]: { ...s[key], [field]: val } }));

  return (
    <div className="bg-pb-surface border border-pb-border p-5 mt-6" data-testid="brain-quota-panel">
      <div className="flex items-center gap-2 mb-1">
        <Brain className="w-5 h-5 text-cyan-300" />
        <span className="font-display font-bold text-lg uppercase">Brain Training Control Center</span>
      </div>
      <p className="text-xs text-zinc-500 mb-4">Scenarios each tier gets per day/week and the max difficulty level they can reach. 999 = unlimited (GOAT default).</p>
      {!tiers ? <div className="text-sm text-zinc-600">Loading…</div> : (
        <div className="space-y-2">
          {Object.entries(tiers).map(([key, t]) => (
            <div key={key} className="flex flex-wrap items-center gap-3 border border-pb-border/60 px-3 py-2" data-testid={`brain-quota-row-${key}`}>
              <span className="font-display font-black uppercase w-14 text-cyan-300">{t.label}</span>
              <label className="text-[9px] uppercase tracking-widest text-zinc-500">Scenarios
                <input data-testid={`brain-quota-input-${key}`} type="number" min="0" max="999" value={t.quota}
                  onChange={(e) => set(key, "quota", e.target.value)} className={`${inp} ml-1.5`} />
              </label>
              <label className="text-[9px] uppercase tracking-widest text-zinc-500">Per
                <select value={t.period} onChange={(e) => set(key, "period", e.target.value)} data-testid={`brain-period-${key}`}
                  className="bg-pb-elevated border border-pb-border px-2 py-1.5 text-xs text-white ml-1.5">
                  <option value="day">Day</option><option value="week">Week</option>
                </select>
              </label>
              <label className="text-[9px] uppercase tracking-widest text-zinc-500">Max Level
                <select value={t.max_lvl} onChange={(e) => set(key, "max_lvl", e.target.value)} data-testid={`brain-maxlvl-${key}`}
                  className="bg-pb-elevated border border-pb-border px-2 py-1.5 text-xs text-white ml-1.5">
                  {[1, 2, 3, 4, 5].map((l) => <option key={l} value={l}>L{l}</option>)}
                </select>
              </label>
              <label className="text-[9px] uppercase tracking-widest text-zinc-500">Extra Q Cost
                <input data-testid={`brain-extra-cost-${key}`} type="number" min="0" step="0.5" value={t.extra_cost}
                  onChange={(e) => set(key, "extra_cost", e.target.value)} className={`${inp} ml-1.5`} />
                <span className="ml-1 text-amber-400">BLC</span>
              </label>
              {Number(t.quota) >= 999 && <span className="text-[9px] uppercase tracking-widest text-amber-400">Unlimited</span>}
              <button data-testid={`brain-quota-save-${key}`} disabled={busy} onClick={() => save(key)}
                className="ml-auto border border-pb-cyan/60 text-pb-cyan hover:bg-pb-cyan hover:text-black px-3 py-1.5 text-[10px] font-black uppercase tracking-widest transition-colors disabled:opacity-50">
                Save
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
